<template>
  <v-card class="full-height">
    <v-card-title class="text-overline"> PREVIEW </v-card-title>

    <v-card-text>
      <v-chip v-if="duration" color="primary">
        {{ duration }}
      </v-chip>
    </v-card-text>

    <v-card-title class="text-overline">
      Success probability

      <div
        class="text-h3 font-weight-bold"
        :class="`text-${riskAssessment.status.color}`"
      >
        {{ riskAssessment.status.name }}
      </div>

      <div class="text-h6 text-medium-emphasis font-weight-regular">
        {{ formatNumber(riskAssessment.estimated.reach) }} Total Potential Reach
      </div>
    </v-card-title>
    <br />
    <v-card-text>
      <!-- <div
        :style="`right: calc(${100 - estimatedVsTargetPercent}% + 2px)`"
        class="position-absolute mt-n8 text-caption"
        :class="`text-${riskAssessment.status.color}`"
      >
        Goal
      </div> -->
      <v-progress-linear
        :color="riskAssessment.status.color"
        height="22"
        :model-value="estimatedVsTargetPercent"
        rounded="lg"
      >
        <v-badge
          :style="`right: ${100 - estimatedVsTargetPercent}%`"
          class="position-absolute"
          color="white"
          dot
          inline
        ></v-badge>
      </v-progress-linear>

      <div class="d-flex justify-space-between py-3">
        <span
          class="font-weight-medium"
          :class="`text-${riskAssessment.status.color}`"
        >
          {{ formatNumber(riskAssessment.target.reach) }} Target Reach
        </span>

        <span class="text-medium-emphasis">
          {{ formatNumber(moreReach) }} More</span
        >
      </div>
    </v-card-text>

    <v-divider />

    <v-slide-y-transition>
      <v-table
        density="compact"
        v-if="modelValue.goal && modelValue.goal.length"
      >
        <tbody>
          <tr v-for="item in modelValue.goal" :key="item.id">
            <td>{{ item.name }}</td>
            <td class="text-primary">
              <span v-if="item.unit === '$'">
                {{ formatPrice(item.value || 0) }}
              </span>
              <span v-else-if="item.unit === 'count'">
                {{ formatNumber(item.value || 0) }}
              </span>

              <span v-else> {{ item.value || 0 }} {{ item.unit }} </span>
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-slide-y-transition>

    <v-list-item
      append-icon="mdi-chevron-right"
      lines="two"
      subtitle="Details and agreement"
      link
    />
  </v-card>
</template>


<script>
import { CampaignAPIInstance } from "@/api";
import { FormatterHelper } from "@/helpers/formatter.helper";
import moment from "moment";

export default {
  name: "CampaignCreationPreview",
  props: {
    modelValue: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      riskAssessment: {
        status: {
          name: "Unknown",
          description:
            "Risk Assessment has not been performed yet. To do so, please fill in the form.",
          color: "grey-darken-3",
        },
        target: {
          impressions: 0,
          reach: 0,
        },
        estimated: {
          impressions: 0,
          reach: 0,
        },
      },
    };
  },

  computed: {
    duration() {
      return this.modelValue && this.modelValue.settings
        ? this.modelValue.settings.startDate && this.modelValue.settings.endDate
          ? `${moment(this.modelValue.settings.startDate).format(
              "MMMM Do"
            )} - ${moment(this.modelValue.settings.endDate).format("MMMM Do")}`
          : ""
        : "";
    },
    estimatedVsTargetPercent() {
      if (
        this.riskAssessment.target.reach &&
        this.riskAssessment.estimated.reach
      ) {
        return (
          (this.riskAssessment.target.reach /
            this.riskAssessment.estimated.reach) *
          100
        );
      }
      return 0;
    },
    moreReach() {
      if (
        this.riskAssessment.target.reach &&
        this.riskAssessment.estimated.reach
      ) {
        return this.riskAssessment.estimated.reach >
          this.riskAssessment.target.reach
          ? this.riskAssessment.estimated.reach -
              this.riskAssessment.target.reach
          : 0;
      }
      return 0;
    },
  },
  watch: {
    "modelValue.targeting"(newVal) {
      if (newVal) {
        this.predict();
      }
    },
    "modelValue.budget.daily"(newVal) {
      if (newVal) {
        this.predict();
      }
    },
    "modelValue.budget.cpm"(newVal) {
      if (newVal) {
        this.predict();
      }
    },
    "modelValue.settings.startDate": function (newVal) {
      if (newVal) {
        this.predict();
      }
    },
    "modelValue.settings.endDate": function (newVal) {
      if (newVal) {
        this.predict();
      }
    },
    "modelValue.settings.frequencyCap": function (newVal) {
      if (newVal) {
        this.predict();
      }
    },
  },
  methods: {
    formatPrice(value) {
      return FormatterHelper.formatPriceWithoutFloat(value);
    },
    formatDate(date) {
      return FormatterHelper.da(date);
    },
    formatNumber(n) {
      return FormatterHelper.formatNumber(n);
    },

    async predict() {
      if (
        !(
          this.modelValue.budget &&
          this.modelValue.budget.total &&
          this.modelValue.budget.daily &&
          this.modelValue.budget.cpm
        ) ||
        !(
          this.modelValue.settings &&
          this.modelValue.settings.startDate &&
          this.modelValue.settings.endDate &&
          this.modelValue.settings.frequencyCap
        )
      ) {
        return;
      }

      const predictionResult =
        await CampaignAPIInstance.Stats.predictPerformance(this.modelValue);
      this.riskAssessment = predictionResult;

      this.$emit("update:modelValue", {
        ...this.modelValue,
        risk: predictionResult.status,
        predictionResult,
      });
    },
  },
};
</script>